/**
 * @architecture-header 1
 * @layer module-ui
 * @domain settings-users
 * @responsibility Render the SET-F05 Users pane from backend-authoritative organization-member hooks.
 * @state-owner TanStack Query
 * @allowed-imports same-module hooks/components
 * @forbidden direct API client; hardcoded organization data; email/provider invite flow (SET-F06)
 * @public-api UsersSettingsPage
 * @side-effects invokes authorized same-module commands and cache invalidation
 * @tests UsersSettingsPage.test.tsx, UsersSettingsSurface.test.tsx
 */

import {
  useCreateOrganizationMember,
  useOrganizationMembers,
  useOrganizationMembersAccess,
  useUpdateOrganizationMember,
} from '../api/settings-users.queries';
import { UsersSettingsSurface } from './UsersSettingsSurface';

function errorCodeOf(error: unknown): string | null {
  return error && typeof error === 'object' && 'code' in error ? String(error.code) : null;
}

export function UsersSettingsPage({ mobile }: { mobile: boolean }) {
  const access = useOrganizationMembersAccess();
  const canManage = access.data?.canManage ?? false;
  const membersQuery = useOrganizationMembers(access.data?.organizationId ?? null, canManage);
  const create = useCreateOrganizationMember();
  const update = useUpdateOrganizationMember();

  const commandError = create.error ?? update.error;
  const commandSucceeded = create.isSuccess || update.isSuccess;
  const errorCode = access.isError
    ? (errorCodeOf(access.error) ?? 'ORGANIZATION_MEMBERS_UNAVAILABLE')
    : access.isSuccess && !canManage
      ? 'ORGANIZATION_MEMBER_ADMIN_REQUIRED'
      : (errorCodeOf(commandError) ?? errorCodeOf(membersQuery.error));

  return (
    <UsersSettingsSurface
      canManage={canManage}
      commandError={commandError}
      commandSucceeded={commandSucceeded}
      directory={membersQuery.data ?? null}
      errorCode={errorCode}
      loading={access.isPending || (canManage && membersQuery.isPending)}
      mobile={mobile}
      onCreate={(input) => create.mutate(input)}
      onRetry={() => {
        create.reset();
        update.reset();
        void access.refetch();
        void membersQuery.refetch();
      }}
      onUpdate={(member, input) => update.mutate({ userId: member.userId, ...input })}
      saving={create.isPending || update.isPending}
    />
  );
}
